import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, Search } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuthContext } from '@/contexts/AuthContext';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { isPaidPlan } from '@/lib/planos';
import { useRecorrencias } from '@/hooks/useRecorrencias';
import { RecorrenciaCard } from '@/components/recorrencias/RecorrenciaCard';
import {
  RecorrenciasFiltros,
  RECORRENCIAS_FILTROS_INICIAL,
  type RecorrenciasFiltrosState,
} from '@/components/recorrencias/RecorrenciasFiltros';

export default function Recorrencias() {
  useFeatureTracking('recorrencias');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization } = useAuthContext();
  const planoPago = isPaidPlan(organization?.plano);
  const { data: recorrencias, isLoading } = useRecorrencias();

  const [termoBusca, setTermoBusca] = useState('');
  const [filtros, setFiltros] = useState<RecorrenciasFiltrosState>(RECORRENCIAS_FILTROS_INICIAL);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const filtradas = useMemo(() => {
    if (!recorrencias) return [];
    const termo = termoBusca.trim().toLowerCase();

    return recorrencias.filter((r) => {
      if (filtros.status === 'ativa' && !r.ativa) return false;
      if (filtros.status === 'pausada' && r.ativa) return false;
      if (filtros.frequencia && r.frequencia !== filtros.frequencia) return false;

      if (filtros.prestadorId === '__none__') {
        if (r.prestador_id) return false;
      } else if (filtros.prestadorId) {
        if (r.prestador_id !== filtros.prestadorId) return false;
      }

      if (termo) {
        const matchCliente = r.cliente?.nome?.toLowerCase().includes(termo);
        const matchTelefone = r.cliente?.telefone?.toLowerCase().includes(termo);
        const matchPrestador = r.prestador?.profile?.nome?.toLowerCase().includes(termo);
        if (!matchCliente && !matchTelefone && !matchPrestador) return false;
      }

      return true;
    });
  }, [recorrencias, termoBusca, filtros]);

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('recurrences.title')}
          badge={
            <span className="ow-badge ow-badge--outline">{t('recurrences.newBadge')}</span>
          }
          acoes={
            <>
              {planoPago && (
                <Popover open={filtrosAbertos} onOpenChange={setFiltrosAbertos}>
                  <PopoverTrigger asChild>
                    <button type="button" className="ow-btn ow-btn--outline ow-btn--sm">
                      <Filter size={14} />
                      {t('common.filters')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80">
                    <RecorrenciasFiltros filtros={filtros} onChange={setFiltros} />
                  </PopoverContent>
                </Popover>
              )}
              <UserMenu />
            </>
          }
        />

        {!planoPago ? (
          <EmptyState
            titulo={t('recurrences.locked.title')}
            descricao={t('recurrences.locked.description')}
            acao={
              <Button onClick={() => navigate('/configuracoes/plano')}>
                {t('recurrences.locked.cta')}
              </Button>
            }
          />
        ) : (
          <>
            <div className="dst-toolbar">
              <label className="dst-toolbar__search">
                <Search size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                <input
                  type="text"
                  placeholder={t('recurrences.searchPlaceholder', 'Buscar por cliente, prestador...')}
                  value={termoBusca}
                  onChange={(e) => setTermoBusca(e.target.value)}
                />
              </label>
              <span className="dst-toolbar__count">
                {filtradas.length} {t('common.of', 'de')} {recorrencias?.length || 0}
              </span>
            </div>

            {isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-44 w-full rounded-2xl" />
                ))}
              </div>
            ) : filtradas.length === 0 ? (
              <EmptyState
                titulo={
                  recorrencias && recorrencias.length > 0
                    ? t('common.noResults')
                    : t('recurrences.empty.title')
                }
                descricao={
                  recorrencias && recorrencias.length > 0
                    ? t('recurrences.empty.tryAdjusting', 'Tenta ajustar os filtros ou a busca.')
                    : t('recurrences.empty.description')
                }
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtradas.map((r) => (
                  <RecorrenciaCard key={r.id} recorrencia={r} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
